import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import Draggable from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'
import { notification } from 'antd'
import PropTypes from 'prop-types'
import * as AntIcons from '@ant-design/icons'

import { sizes } from '@qflow/theme'
import {
  Container,
  InnerContainer,
  ActionStripCard,
  ActionStripButtonGroup
} from 'portal/components/CommonStyles/ActionStripStyledComponents'

import {
  icons,
  Clickable,
  Text,
  Padding,
  ColourButton,
  Spacer,
  Spinner
} from 'portal/lib/primitives'
import {
  assignRecords,
  unassignRecords,
  tagRecords,
  untagRecords
} from 'portal/store/events/actions'
import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { AssignmentStrip } from 'portal/lib/sharedComponents/AssignmentStrip'
import { UnassignmentStrip } from 'portal/lib/sharedComponents/UnassignmentStrip'
import { TaggingStrip } from 'portal/lib/sharedComponents/TaggingStrip'
import { UntaggingStrip } from 'portal/lib/sharedComponents/UntaggingStrip'

export function ActionStrip({
  recordIds,
  recordAssigneeMapping,
  recordTagsMapping,
  openMode,
  onClose,
  onApply
}) {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const [showAssignmentStrip, setShowAssignmentStrip] = useState(false)
  const [showUnassignmentStrip, setShowUnassignmentStrip] = useState(false)
  const [showTaggingStrip, setShowTaggingStrip] = useState(false)
  const [showUntaggingStrip, setShowUntaggingStrip] = useState(false)
  const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const draggableRef = useRef(null)

  const projectUsers = useSelector(state => state.projectUsers)
  const assign = useSelector(state => state.events.assign)
  const unassign = useSelector(state => state.events.unassign)
  const dismissal = useSelector(state => state.events.dismissal)
  const tag = useSelector(state => state.events.tag)
  const untag = useSelector(state => state.events.untag)

  // Use selected Records to create a set of unique users who are assigned to 1 or more events/records
  const relatedAssignees = useMemo(() => {
    const assignedUsers = {}
    recordIds.forEach(record => {
      if (recordAssigneeMapping[record]) {
        recordAssigneeMapping[record].forEach(assignee => {
          assignedUsers[assignee.userId] = {
            userId: assignee.userId,
            displayName: assignee.userName,
            email: projectUsers.users[`${projectId}_${assignee.userId}`]?.email
          }
        })
      }
    })
    return assignedUsers
  }, [projectId, projectUsers.users, recordAssigneeMapping, recordIds])

  const hasRelatedAssignees = Object.keys(relatedAssignees).length > 0

  const handleAssignBtnClick = () => {
    setShowUnassignmentStrip(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(false)
    setShowAssignmentStrip(!showAssignmentStrip)
  }

  const handleUnassignBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(false)
    setShowUnassignmentStrip(!showUnassignmentStrip)
  }

  const handleTagBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowUntaggingStrip(false)
    setShowTaggingStrip(!showTaggingStrip)
  }

  const handleUntagBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(!showUntaggingStrip)
  }

  const handleRecordsAssign = useCallback(
    assignTo => {
      dispatch(
        assignRecords(accountId, projectId, recordIds, assignTo, () => {
          onClose?.(true)
          onApply()
        })
      )
      setShowAssignmentStrip(false)
    },
    [accountId, dispatch, onApply, onClose, projectId, recordIds]
  )

  const handleRecordsUnassign = useCallback(
    unassignUsers => {
      dispatch(
        unassignRecords(accountId, projectId, recordIds, unassignUsers, () => {
          onClose?.(true)
          onApply()
        })
      )
      setShowUnassignmentStrip(false)
    },
    [accountId, dispatch, onApply, onClose, projectId, recordIds]
  )

  const handleRecordsTag = useCallback(
    tags => {
      dispatch(
        tagRecords(
          accountId,
          projectId,
          recordIds,
          tags,
          () => {
            onClose?.(true)
            onApply()
          },
          () => {
            notification.error({
              message: 'Unable to tag records',
              description:
                'A conflict occurred while updating records. Please refresh and try again.'
            })
          }
        )
      )
      dispatch(
        trackMixpanel('frontend_record_tags_added', {
          project_id: projectId,
          account_id: accountId,
          record_count: recordIds.length,
          tag_count: tags.length,
          record_type: moduleName
        })
      )
      setShowTaggingStrip(false)
    },
    [accountId, dispatch, onApply, onClose, projectId, recordIds, moduleName]
  )

  const handleRecordsUntag = useCallback(
    tags => {
      dispatch(
        untagRecords(
          accountId,
          projectId,
          recordIds,
          tags,
          () => {
            onClose?.(true)
            onApply()
          },
          () => {
            notification.error({
              message: 'Unable to untag records',
              description:
                'A conflict occurred while updating records. Please refresh and try again.'
            })
          }
        )
      )
      dispatch(
        trackMixpanel('frontend_record_tags_removed', {
          project_id: projectId,
          account_id: accountId,
          record_count: recordIds.length,
          tag_count: tags.length,
          record_type: moduleName
        })
      )
      setShowUntaggingStrip(false)
    },
    [accountId, dispatch, onApply, onClose, projectId, recordIds, moduleName]
  )

  const working =
    dismissal.loading ||
    assign.loading ||
    unassign.loading ||
    tag.loading ||
    untag.loading
  const visible = recordIds?.length > 0
  const showUnassignBtn = hasRelatedAssignees

  // Collect unique tags from selected records for untag suggestions
  const relatedTags = useMemo(() => {
    const tagSet = new Set()
    recordIds.forEach(record => {
      if (recordTagsMapping?.[record]) {
        recordTagsMapping[record].forEach(t => tagSet.add(t))
      }
    })
    return Array.from(tagSet).sort()
  }, [recordIds, recordTagsMapping])

  const showUntagBtn = relatedTags.length > 0
  const showAssigneeOptions = openMode !== 'tags'
  const showTagOptions = openMode !== 'assignees'

  useEffect(() => {
    if (showUnassignmentStrip && !hasRelatedAssignees) {
      setShowUnassignmentStrip(false)
      if (openMode !== 'tags') {
        setShowAssignmentStrip(true)
      }
    }
  }, [hasRelatedAssignees, openMode, showUnassignmentStrip])

  useEffect(() => {
    if (!recordIds?.length) {
      return
    }

    if (openMode === 'assignees') {
      setShowAssignmentStrip(true)
      setShowUnassignmentStrip(false)
      setShowTaggingStrip(false)
      setShowUntaggingStrip(false)
      return
    }

    if (openMode === 'tags') {
      setShowAssignmentStrip(false)
      setShowUnassignmentStrip(false)
      setShowTaggingStrip(true)
      setShowUntaggingStrip(false)
      return
    }
  }, [openMode, recordIds])

  return (
    <Container $visible={visible}>
      {working && <Spinner />}
      <Draggable bounds="body" nodeRef={draggableRef}>
        <InnerContainer ref={draggableRef} style={{ cursor: 'move' }}>
          <ActionStripCard>
            <Padding noVertical>
              <Text bold size={sizes.MEDIUM}>
                {recordIds.length} selected
              </Text>
            </Padding>

            {isProjectAdmin && showAssigneeOptions && (
              <ActionStripButtonGroup>
                <ColourButton.Basic
                  type="button"
                  onClick={handleAssignBtnClick}
                  disabled={working}
                  data-testid="Assign"
                  className={showAssignmentStrip ? 'selected' : ''}
                  style={{
                    borderRadius: showUnassignBtn ? '5px 0 0 5px' : '5px'
                  }}
                >
                  <icons.AddPerson colour="currentColor" size={sizes.MEDIUM} />
                  <Spacer.Fine />
                  Assign
                </ColourButton.Basic>

                {showUnassignBtn && (
                  <ColourButton.Basic
                    type="button"
                    data-testid="Unassign"
                    onClick={handleUnassignBtnClick}
                    disabled={working}
                    className={showUnassignmentStrip ? 'selected' : ''}
                    style={{ borderLeft: 'none', borderRadius: '0 5px 5px 0' }}
                  >
                    <icons.RemovePerson
                      colour="currentColor"
                      size={sizes.MEDIUM}
                    />
                    <Spacer.Fine />
                    Unassign
                  </ColourButton.Basic>
                )}
              </ActionStripButtonGroup>
            )}

            {showTagOptions && (
              <ActionStripButtonGroup
                style={{ marginLeft: isProjectAdmin ? '0.5rem' : 0 }}
              >
                <ColourButton.Basic
                  type="button"
                  id="Tag"
                  onClick={handleTagBtnClick}
                  disabled={working}
                  className={showTaggingStrip ? 'selected' : ''}
                  style={{
                    borderRadius: showUntagBtn ? '5px 0 0 5px' : '5px'
                  }}
                >
                  <AntIcons.TagOutlined />
                  <Spacer.Fine />
                  Tag
                </ColourButton.Basic>

                {showUntagBtn && (
                  <ColourButton.Basic
                    type="button"
                    id="Untag"
                    onClick={handleUntagBtnClick}
                    disabled={working}
                    className={showUntaggingStrip ? 'selected' : ''}
                    style={{ borderLeft: 'none', borderRadius: '0 5px 5px 0' }}
                  >
                    <AntIcons.TagsOutlined />
                    <Spacer.Fine />
                    Untag
                  </ColourButton.Basic>
                )}
              </ActionStripButtonGroup>
            )}

            <Spacer.Small />

            <Clickable
              size="small"
              title="Close"
              onClick={() => onClose(false)}
              disabled={working}
            >
              <icons.Cross size={sizes.X_SMALL} colour="inherit" />
            </Clickable>
          </ActionStripCard>

          {isProjectAdmin && showAssignmentStrip && (
            <AssignmentStrip
              users={projectUsers.users}
              onApply={handleRecordsAssign}
              recordCount={recordIds.length}
            />
          )}

          {isProjectAdmin && showUnassignmentStrip && (
            <UnassignmentStrip
              users={relatedAssignees}
              onApply={handleRecordsUnassign}
              recordCount={recordIds.length}
            />
          )}

          {showTaggingStrip && (
            <TaggingStrip
              onApply={handleRecordsTag}
              recordCount={recordIds.length}
            />
          )}

          {showUntaggingStrip && (
            <UntaggingStrip
              relatedTags={relatedTags}
              onApply={handleRecordsUntag}
              recordCount={recordIds.length}
            />
          )}
        </InnerContainer>
      </Draggable>
    </Container>
  )
}

ActionStrip.propTypes = {
  recordIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  recordTagsMapping: PropTypes.object,
  openMode: PropTypes.oneOf(['assignees', 'tags', null]),
  onClose: PropTypes.func
}
